import { useEffect, useState } from 'react';
import { formatPercent, formatRial, type PriceDriftReport } from '@kia/shared';
import { api, errMsg } from '../api/client';
import { postPrice } from '../api/priceWrite';
import { Button, Card, Chip, Kpi, useConfirm, useToast } from '../ui';

/**
 * پایش انحراف قیمت — the price-drift monitor (the #1 pricing-accuracy lever). Material is ~76% of a motor's
 * cost, so a stale material price silently under-costs the motor and floors its price too low. This lists,
 * biggest-rial-impact first, the materials whose BOOK price lags the most recent ACTUAL PAID price (the
 * material's own purchase, or a group peer's for group-priced materials — data already in po_receipts). One
 * click adopts the paid price. The headline `netBiasPct` says whether the whole book under/over-costs.
 */
export function PriceDriftCard({ canEdit, onAdopt }: { canEdit: boolean; onAdopt?: () => void }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [report, setReport] = useState<PriceDriftReport | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  function load() {
    void api<PriceDriftReport>('/api/prices/drift').then((r) => {
      if (r.ok) setReport(r.body as PriceDriftReport);
    });
  }
  useEffect(load, []);

  async function adopt(code: number, price: number) {
    setBusy(code);
    // Route through the shared guard: adopting a paid price is the path MOST likely to trip the write-time
    // fat-finger / OCR-10× confirm (it's an OCR'd purchase price), so it must honor `confirmRequired` like
    // every other price write — not POST raw.
    const r = await postPrice('/api/prices/mat', { code, price }, confirm);
    setBusy(null);
    if (!r.ok) {
      // A confirmRequired refusal means the user DECLINED the jump confirm — stay silent, don't cry error.
      const declined = (r.body as { details?: { confirmRequired?: boolean } })?.details
        ?.confirmRequired;
      if (!declined) toast(errMsg(r, 'خطا در ثبت قیمت'), 'error');
      return;
    }
    toast('قیمت خرید به‌عنوان قیمت ماده ثبت شد ✓');
    load();
    onAdopt?.();
  }

  if (!report) return null;

  // Net bias: positive ⇒ the book UNDER-costs (prices set too low) — the pricing-strategy risk to flag.
  const bias = report.netBiasPct;
  const biasTone = Math.abs(bias) < 0.005 ? 'neutral' : bias > 0 ? 'warn' : 'info';
  const biasLabel =
    Math.abs(bias) < 0.005
      ? 'کتاب قیمت هم‌راستا با خرید'
      : bias > 0
        ? `کم‌برآورد بها ${formatPercent(bias)}`
        : `بیش‌برآورد بها ${formatPercent(-bias)}`;

  const title = (
    <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center' }}>
      پایش انحراف قیمت مواد پرمصرف
      <Chip tone={biasTone}>{biasLabel}</Chip>
    </span>
  );

  return (
    <Card title={title}>
      <p style={{ margin: '0 0 10px', color: 'var(--ink-2)', lineHeight: 1.9 }}>
        موادی که «قیمت کتاب» آن‌ها از آخرین «قیمت خرید واقعی» عقب افتاده — مرتب‌شده بر اساس اثر
        ریالی (سهم بها × انحراف). چون بهای مواد بزرگ‌ترین بخش بهای موتور است، این‌ها بیشترین خطا را
        به قیمت‌گذاری وارد می‌کنند. سیگنال یا از «خرید همین ماده» است یا از «خرید هم‌گروه» (برای
        موادی که با قیمت گروه محاسبه می‌شوند). با یک کلیک، قیمت خرید به‌عنوان قیمت اختصاصی ماده ثبت
        می‌شود.
      </p>

      <div className="kpi-grid" style={{ marginBottom: 12 }}>
        <Kpi
          label="مورد نیازمند بازبینی"
          value={report.flaggedCount.toLocaleString('fa-IR')}
          tone={report.flaggedCount ? 'bad' : 'good'}
        />
        <Kpi
          label="سوگیری خالص بها"
          value={formatPercent(bias)}
          tone={bias > 0.005 ? 'bad' : 'good'}
        />
        <Kpi label="آستانهٔ انحراف" value={formatPercent(report.threshold)} />
      </div>

      {/* What the two headlines above could NOT see. Both are measured over the priced subset, so the
          scope is stated next to them rather than left for the reader to assume away. */}
      {report.unpricedSignalMaterials > 0 || report.unpricedCostMaterials > 0 ? (
        <p style={{ margin: '0 0 12px', color: 'var(--ink-3)', lineHeight: 1.9 }}>
          دامنهٔ اندازه‌گیری:
          {report.unpricedSignalMaterials > 0 ? (
            <>
              {' '}
              خرید {report.unpricedSignalMaterials} ماده «قیمت تأییدنشده» دارد و به‌عنوان سیگنال
              شمرده نشده (عدد روی رسید، قیمت سفارش است نه پول تأییدشده)؛ این مواد ممکن است انحراف
              واقعی داشته باشند و علامت نخورند. رفع: «ثبت قیمت» روی همان رسید در «تاریخچهٔ خرید».
            </>
          ) : null}
          {report.unpricedCostMaterials > 0 ? (
            <>
              {' '}
              {report.unpricedCostMaterials} مادهٔ مصرفی هیچ قیمت کتابی ندارند (نه اختصاصی، نه
              گروهی) و بیرون از مخرج «سهم بها» هستند؛ پس «سوگیری خالص» روی همان بخشِ قیمت‌دار
              وزن‌دهی شده.
            </>
          ) : null}
        </p>
      ) : null}

      {report.rows.length === 0 ? (
        <p style={{ color: 'var(--ink-3)' }}>
          هیچ انحراف بالای آستانه‌ای یافت نشد — قیمت‌های کتاب با خریدهای اخیر می‌خوانند.
        </p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>ماده</th>
                <th>قیمت کتاب</th>
                <th>قیمت خرید</th>
                <th>سیگنال</th>
                <th>انحراف</th>
                <th>سهم بها</th>
                {canEdit ? <th></th> : null}
              </tr>
            </thead>
            <tbody>
              {report.rows.map((r) => (
                <tr key={r.code}>
                  <td>
                    {r.name}
                    <span style={{ color: 'var(--ink-3)', fontSize: '.85em' }}>
                      {' '}
                      ({r.code} — {r.group}
                      {r.unit ? `/${r.unit}` : ''})
                    </span>
                  </td>
                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatRial(r.book)}
                    <span style={{ color: 'var(--ink-3)', fontSize: '.8em' }}>
                      {' '}
                      {r.source === 'group' ? '(گروه)' : '(ماده)'}
                      {r.bookDate ? ` — ${r.bookDate}` : ''}
                    </span>
                  </td>
                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>{formatRial(r.signal)}</td>
                  <td style={{ color: 'var(--ink-2)', fontSize: '.9em' }}>
                    {r.signalFrom === 'own' ? 'خرید این ماده' : 'خرید هم‌گروه'}
                    <br />
                    {r.signalDate}
                    {r.signalSupplier ? ` — ${r.signalSupplier}` : ''}
                  </td>
                  <td
                    style={{
                      fontVariantNumeric: 'tabular-nums',
                      color: r.divergencePct > 0 ? 'var(--red)' : 'var(--green)',
                      fontWeight: 700,
                    }}
                  >
                    {r.divergencePct > 0 ? '+' : ''}
                    {formatPercent(r.divergencePct)}
                  </td>
                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatPercent(r.spendShare)}
                  </td>
                  {canEdit ? (
                    <td>
                      <Button
                        variant="secondary"
                        onClick={() => void adopt(r.code, r.signal)}
                        disabled={busy === r.code}
                      >
                        {busy === r.code ? '…' : 'اعمال قیمت خرید'}
                      </Button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
